/* Section-scoped: never touches Horizon product forms, cart, or checkout. */
if (!customElements.get('fm-motion')) {
  customElements.define('fm-motion', class extends HTMLElement {
    connectedCallback() {
      this.cleanup?.abort();
      this.cleanup = new AbortController();
      const { signal } = this.cleanup;
      const video = this.querySelector('video');
      if (!video) return;
      const button = this.querySelector('.fm-play');
      const status = this.querySelector('.fm-motion-status');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const scrollMode = this.dataset.mode === 'scroll';
      let loaded = false, active = false, frame = 0, userPaused = false, failed = false;
      video.controls = false;
      video.muted = true;
      const fallback = () => {
        failed = true;
        this.classList.remove('fm-ready');
        button.hidden = true;
        video.controls = true;
        status.textContent = 'Animation unavailable. You can try the video controls.';
      };
      const load = () => {
        if (loaded) return;
        loaded = true;
        // preload="none" protects initial page load; request media only near the viewport.
        video.preload = 'auto';
        video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
        video.load();
      };
      const seek = () => {
        frame = 0;
        if (failed || !active || reduced.matches || !scrollMode || !Number.isFinite(video.duration) || video.seeking) return;
        const rect = this.getBoundingClientRect();
        const stage = this.querySelector('.fm-motion-stage');
        const top = parseFloat(getComputedStyle(stage).top) || 0;
        const travel = Math.max(1, this.offsetHeight - stage.offsetHeight);
        const progress = Math.min(1, Math.max(0, (top - rect.top) / travel));
        const target = progress * Math.max(0, video.duration - .04);
        if (Math.abs(video.currentTime - target) > .035) video.currentTime = target;
      };
      const queue = () => { if (!frame) frame = requestAnimationFrame(seek); };
      const play = () => {
        load();
        button.hidden = false;
        return video.play().then(() => {
          if (!this.isConnected || !active) { video.pause(); return; }
          if (scrollMode && !reduced.matches) {
            video.pause();
            this.classList.add('fm-ready');
            queue();
          }
          button.textContent = 'Pause animation';
          button.hidden = scrollMode && !reduced.matches;
        }).catch(() => {
          if (scrollMode) this.classList.remove('fm-ready');
          button.hidden = false;
          button.textContent = 'Play animation';
        });
      };
      button.addEventListener('click', () => {
        if (!video.paused) {
          userPaused = true;
          video.pause(); button.textContent = 'Play animation';
        } else { userPaused = false; play(); }
      }, { signal });
      video.addEventListener('loadedmetadata', () => {
        if (!reduced.matches && active && !userPaused) play();
        if (reduced.matches) button.hidden = false;
      }, { signal });
      video.addEventListener('seeked', queue, { signal });
      video.addEventListener('error', fallback, { signal });
      // Horizon scrolls .page-wrapper, not window. Scroll does not bubble:
      // capture observes both nested theme/editor scrollers and document scroll.
      document.addEventListener('scroll', queue, { capture: true, passive: true, signal });
      window.addEventListener('resize', queue, { passive: true, signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) video.pause();
        else if (active && !reduced.matches && !userPaused && !failed) play();
      }, { signal });
      reduced.addEventListener('change', () => {
        video.pause();
        this.classList.remove('fm-ready');
        button.hidden = false;
        button.textContent = 'Play animation';
        if (!reduced.matches && active && !userPaused && !failed) play();
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        active = entries[0].isIntersecting;
        if (active) {
          load();
          if (!reduced.matches && !userPaused && !failed) play();
          if (scrollMode) queue();
        } else video.pause();
      }, { rootMargin: '100px' });
      this.observer.observe(this);
      this.stopFrame = () => cancelAnimationFrame(frame);
      if (reduced.matches) button.hidden = false;
    }
    disconnectedCallback() {
      this.cleanup?.abort(); this.observer?.disconnect(); this.stopFrame?.();
      this.querySelector('video')?.pause();
    }
  });
}
